import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
    CloudArrowUp, Trash3, StarFill, ArrowLeftShort, ArrowRightShort, XLg
} from 'react-bootstrap-icons';
import CarImage from '../../../common/ui/CarImage';
import ConfirmModal from '../../../common/ui/ConfirmModal';
import notify, { currentLanguage } from '../../../common/ui/notify';
import language from '../../../data/language';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif', 'image/gif'];
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_FILES_PER_REQUEST = 30;

/**
 * Gallery editor for one car. Cover photo and order are just positions in the
 * `images` array, and every change is written to the server immediately —
 * nothing lives only in the browser.
 */
const CarImagesManager = ({carId, images = [], onChange}) => {
    const lang = currentLanguage();
    const strings = language[lang].carForm;
    const common = language[lang].common;

    const [pending, setPending] = useState([]);   // {file, preview}
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [busy, setBusy] = useState(false);
    const [confirmTarget, setConfirmTarget] = useState(null);
    const inputRef = useRef(null);
    const previewUrlsRef = useRef([]);

    // Object URLs leak unless they are released — but only on unmount. Tying
    // this to `pending` would revoke the previews of images already queued the
    // moment another file is added, blanking the thumbnails that are still shown.
    useEffect(() => () => {
        previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        previewUrlsRef.current = [];
    }, []);

    const trackPreview = (url) => {
        previewUrlsRef.current.push(url);
        return url;
    };

    const releasePreview = (url) => {
        URL.revokeObjectURL(url);
        previewUrlsRef.current = previewUrlsRef.current.filter((item) => item !== url);
    };

    const addFiles = (fileList) => {
        const files = Array.from(fileList || []);
        const accepted = [];

        files.forEach((file) => {
            if (!ALLOWED_TYPES.includes(file.type)) {
                notify.error(`${file.name}: ${strings.invalid_type}`);
                return;
            }
            if (file.size > MAX_FILE_SIZE) {
                notify.error(`${file.name}: ${strings.too_large}`);
                return;
            }
            accepted.push({file, preview: trackPreview(URL.createObjectURL(file))});
        });

        if (accepted.length === 0) return;

        setPending((prev) => {
            const next = [...prev, ...accepted];
            if (next.length > MAX_FILES_PER_REQUEST) {
                notify.warning(strings.too_many || common.error_title);
                return next.slice(0, MAX_FILES_PER_REQUEST);
            }
            return next;
        });
    };

    const removePending = (index) => {
        setPending((prev) => {
            const target = prev[index];
            if (target) releasePreview(target.preview);
            return prev.filter((_, position) => position !== index);
        });
    };

    const upload = async () => {
        if (pending.length === 0 || uploading) return;

        const payload = new FormData();
        pending.forEach((item) => payload.append("images", item.file));

        setUploading(true);
        setProgress(0);

        try {
            const res = await axios.post(`/api/cars/${carId}/images`, payload, {
                onUploadProgress: (event) => {
                    if (!event.total) return;
                    setProgress(Math.round((event.loaded * 100) / event.total));
                }
            });
            pending.forEach((item) => releasePreview(item.preview));
            setPending([]);
            notify.success(strings.images_uploaded);
            onChange(res.data.images || []);
        } catch (error) {
            // The server removes anything it already wrote when a batch fails,
            // so the local queue is kept for a retry.
            notify.apiError(error);
        } finally {
            setUploading(false);
            setProgress(0);
        }
    };

    /** Persists a new order (position 0 is the cover). */
    const saveOrder = async (nextImages, successMessage) => {
        if (busy) return;
        setBusy(true);
        try {
            const res = await axios.put(`/api/cars/${carId}/mark-as-main-image`, {images: nextImages});
            notify.success(successMessage);
            onChange(res.data.images || nextImages);
        } catch (error) {
            notify.apiError(error);
        } finally {
            setBusy(false);
        }
    };

    const setCover = (index) => {
        if (index === 0) return;
        const next = [images[index], ...images.filter((_, position) => position !== index)];
        saveOrder(next, strings.gallery_updated);
    };

    const move = (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= images.length) return;
        const next = [...images];
        [next[index], next[target]] = [next[target], next[index]];
        saveOrder(next, strings.gallery_updated);
    };

    const confirmDelete = async () => {
        if (confirmTarget === null) return;
        setBusy(true);
        try {
            const res = await axios.delete(`/api/cars/${carId}/images`, {data: {image: confirmTarget}});
            notify.success(strings.image_removed);
            onChange(res.data.images || []);
            setConfirmTarget(null);
        } catch (error) {
            notify.apiError(error);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="car-images-manager">
            <div className="car-images-upload">
                <input
                    ref={inputRef}
                    id="car-images-input"
                    type="file"
                    hidden
                    multiple
                    accept={ALLOWED_TYPES.join(",")}
                    onChange={(e) => {
                        addFiles(e.target.files);
                        // Reset so picking the same file twice still fires onChange.
                        e.target.value = "";
                    }}
                />
                <label htmlFor="car-images-input" className="car-images-dropzone">
                    <CloudArrowUp aria-hidden="true" />
                    <span className="car-images-dropzone-title">{strings.choose_files}</span>
                    <span className="car-images-dropzone-hint">{strings.drop_hint}</span>
                </label>
            </div>

            {pending.length > 0 && (
                <div className="car-images-section">
                    <div className="car-images-section-head">
                        <h4>{strings.pending_images} ({pending.length})</h4>
                        <button type="button" className="cx-btn cx-btn-primary cx-btn-sm" disabled={uploading} onClick={upload}>
                            {uploading
                                ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> {strings.uploading}</>
                                : <><CloudArrowUp aria-hidden="true" /> {strings.upload}</>}
                        </button>
                    </div>

                    {uploading && (
                        <div className="car-images-progress" role="progressbar" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">
                            <div className="car-images-progress-bar" style={{width: `${progress}%`}}></div>
                        </div>
                    )}

                    <div className="car-images-grid">
                        {pending.map((item, index) => (
                            <figure className="car-image-tile is-pending" key={`${item.file.name}-${index}`}>
                                <img src={item.preview} alt="" />
                                <button
                                    type="button"
                                    className="car-image-remove"
                                    aria-label={strings.remove_image}
                                    disabled={uploading}
                                    onClick={() => removePending(index)}
                                >
                                    <XLg aria-hidden="true" />
                                </button>
                            </figure>
                        ))}
                    </div>
                </div>
            )}

            <div className="car-images-section">
                <div className="car-images-section-head">
                    <h4>{strings.saved_images} ({images.length})</h4>
                </div>

                {images.length === 0 ? (
                    <p className="car-images-empty">{strings.no_images_yet}</p>
                ) : (
                    <div className="car-images-grid">
                        {images.map((image, index) => (
                            <figure className={`car-image-tile ${index === 0 ? "is-cover" : ""}`} key={image}>
                                <CarImage src={image} alt="" />

                                {index === 0 && <span className="car-image-cover-badge">{strings.cover}</span>}

                                <div className="car-image-actions">
                                    <button type="button" aria-label={strings.move_left} title={strings.move_left}
                                            disabled={busy || index === 0} onClick={() => move(index, -1)}>
                                        <ArrowLeftShort aria-hidden="true" />
                                    </button>
                                    <button type="button" aria-label={strings.set_cover} title={strings.set_cover}
                                            disabled={busy || index === 0} onClick={() => setCover(index)}>
                                        <StarFill aria-hidden="true" />
                                    </button>
                                    <button type="button" aria-label={strings.move_right} title={strings.move_right}
                                            disabled={busy || index === images.length - 1} onClick={() => move(index, 1)}>
                                        <ArrowRightShort aria-hidden="true" />
                                    </button>
                                    <button type="button" className="is-danger" aria-label={strings.remove_image} title={strings.remove_image}
                                            disabled={busy} onClick={() => setConfirmTarget(image)}>
                                        <Trash3 aria-hidden="true" />
                                    </button>
                                </div>
                            </figure>
                        ))}
                    </div>
                )}
            </div>

            <ConfirmModal
                show={confirmTarget !== null}
                title={strings.delete_image_title}
                message={strings.delete_image_message}
                confirmLabel={common.delete}
                loading={busy}
                onConfirm={confirmDelete}
                onCancel={() => setConfirmTarget(null)}
            />
        </div>
    );
};

export default CarImagesManager;
